/**
 * The timeline's warnings, grouped (moved out of TimelineWarnings,
 * 19/10/2026): one line per kind with the names it concerns, the notes that
 * are not faults apart, the rest as they came.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DayPlanPreview, DropGenPlanWarning } from '../../../drop-generator.types';

export interface GroupedLine {
  headline: string;
  names: string[];
  suffix: string;
}

export interface Bucket {
  headline: string;
  suffix: string;
  name: (w: DropGenPlanWarning, p: DayPlanPreview) => string;
}

const containerName = (w: DropGenPlanWarning, p: DayPlanPreview) =>
  p.containers.find((c) => c.containerId === w.containerId)?.name ?? 'container';

export const GROUPABLE: Record<string, Bucket> = {
  empty_pool: {
    headline: 'No groups linked',
    suffix: 'nothing planned for them today — link a group in their editor.',
    name: containerName,
  },
  capacity_unknown: {
    headline: 'Lead count not fetched',
    suffix: 'amounts are not capacity-checked for these lists.',
    name: (w) => w.message.split(' has no fetched')[0] ?? 'list',
  },
  slot_no_group: {
    headline: 'Drops without a group',
    suffix: 'not planned — give each one a group in Drop slots.',
    // "SEGMENTS: 2 drops have no group — …" → "SEGMENTS (2)"
    name: (w) => w.message.replace(/: (\d+) drops? ha(?:s|ve) no group.*$/, ' ($1)'),
  },
  slot_outside_window: {
    headline: 'Drops outside the window',
    suffix: 'not planned — the window is a hard limit: move them inside, or widen the window (click the Window at the top).',
    // "WARM UP: 3 drops land outside the window 09:00–16:00 (…) — not planned." → "WARM UP (3)"
    name: (w) => w.message.replace(/: (\d+) drops? lands? outside the window.*$/, ' ($1)'),
  },
  container_overlap: {
    headline: 'Overlapping schedule',
    suffix: 'they start before the previous container of their campaign group finished.',
    name: containerName,
  },
};

/** Not faults: said in a muted line, never in the amber banner. */
export const NOTES: Record<string, Bucket> = {
  container_not_today: {
    headline: 'Not today',
    suffix: 'they run on other days — skipped like a switched-off container.',
    // "NIGHT runs Fri–Sat — not today (Tuesday)." → "NIGHT (Fri–Sat)"
    name: (w) => w.message.replace(/ runs (.+?) — not today.*$/, ' ($1)'),
  },
};

export const lines = (buckets: Map<string, string[]>, spec: Record<string, Bucket>): GroupedLine[] =>
  [...buckets].map(([code, names]) => ({ headline: spec[code].headline, names, suffix: spec[code].suffix }));

export function groupWarnings(preview: DayPlanPreview): {
  grouped: GroupedLine[];
  notes: GroupedLine[];
  rest: DropGenPlanWarning[];
} {
  const buckets = new Map<string, string[]>();
  const noted = new Map<string, string[]>();
  const rest: DropGenPlanWarning[] = [];
  for (const warning of preview.warnings) {
    // Ignore retired estimates in cached previews and older saved runs.
    if (warning.code === 'group_runway_low' || warning.code === 'group_runway_unknown') continue;
    const into = NOTES[warning.code] ? noted : buckets;
    const spec = NOTES[warning.code] ?? GROUPABLE[warning.code];
    if (!spec) {
      rest.push(warning);
      continue;
    }
    into.set(warning.code, [...(into.get(warning.code) ?? []), spec.name(warning, preview)]);
  }
  return { grouped: lines(buckets, GROUPABLE), notes: lines(noted, NOTES), rest };
}

